import type { SupabaseClient } from "@supabase/supabase-js";
import { useMemo } from "react";

import {
  fetchCheckpointMaterialVerification,
  materialVerificationAction,
  materialVerificationLabel,
  type CheckpointMaterialVerificationRow,
  type MaterialVerificationStatus,
} from "../data/material-verification";
import { fetchCheckpointRollout, type CheckpointRolloutRow } from "../data/checkpoint-rollout";
import { useReadModel } from "../data/useReadModel";
import { ActiveMaterialBadge } from "./ActiveMaterialInventory";
import { useTokenMintSession } from "./token-mint-session";

export function MaterialVerification({
  client,
  accountId,
  canMint = false,
}: {
  client: SupabaseClient;
  accountId: string;
  canMint?: boolean;
}) {
  const session = useTokenMintSession();
  const mintGeneration = session?.mintGeneration ?? 0;
  const mintEnabled = canMint && session?.enabled === true;

  const { status, data, error } = useReadModel<CheckpointMaterialVerificationRow[]>(
    () => fetchCheckpointMaterialVerification(client, accountId),
    [client, accountId, mintGeneration],
  );
  const rolloutRead = useReadModel<CheckpointRolloutRow[]>(
    () => fetchCheckpointRollout(client, accountId),
    [client, accountId, mintGeneration],
  );

  const rolloutByCheckpointId = useMemo(() => {
    const map = new Map<string, CheckpointRolloutRow>();
    for (const row of rolloutRead.data ?? []) {
      if (row.checkpointId) map.set(row.checkpointId, row);
    }
    return map;
  }, [rolloutRead.data]);

  if (status === "loading" && data === null) {
    return (
      <p className="surface__state" role="status">
        Loading checkpoint material verification…
      </p>
    );
  }

  if (status === "error") {
    return (
      <p className="surface__state surface__state--error" role="alert">
        {error ?? "Could not load checkpoint material verification."}
      </p>
    );
  }

  const rows = [...(data ?? [])].sort((left, right) => {
    const leftRank = verificationRank(left.verificationStatus);
    const rightRank = verificationRank(right.verificationStatus);
    if (leftRank !== rightRank) return leftRank - rightRank;
    return left.checkpointName.localeCompare(right.checkpointName);
  });

  if (rows.length === 0) {
    return (
      <p className="surface__state" role="status">
        No checkpoint material to verify yet. Create checkpoint material from Token minting first.
      </p>
    );
  }

  const nfcVerified = rows.filter((row) =>
    row.verificationStatus === "verified_primary" || row.verificationStatus === "verified_both",
  ).length;
  const bothVerified = rows.filter((row) => row.verificationStatus === "verified_both").length;
  const backupOnly = rows.filter((row) => row.verificationStatus === "verified_backup").length;
  const needsScan = rows.filter((row) => row.verificationStatus === "unverified").length;
  const missingMaterial = rows.filter((row) => row.verificationStatus === "missing_material").length;
  const attentionRows = rows.filter((row) => row.verificationStatus !== "verified_both");
  const readyRows = rows.filter((row) => row.verificationStatus === "verified_both");

  function openMintForRow(row: CheckpointMaterialVerificationRow): void {
    if (!mintEnabled || session === null) return;
    const routeLabel = [row.routeName, row.siteName].filter(Boolean).join(" · ");
    session.openCheckpointConfirm({
      checkpointId: row.checkpointId,
      checkpointName: row.checkpointName,
      routeLabel,
      hasExistingMaterial: row.hasCheckpointMaterial,
      replace: row.hasCheckpointMaterial,
    });
  }

  return (
    <div className="materialverify">
      <div className="materialverify__summary" role="status">
        <strong>
          {nfcVerified} of {rows.length} checkpoints NFC verified
        </strong>
        <span>
          {bothVerified} both methods · {backupOnly} QR backup only · {needsScan} need scan · {missingMaterial}{" "}
          missing material
        </span>
      </div>
      <p className="materialverify__note">
        Derived from QR/NFC scans received after the latest active material was created. NFC is the
        primary field proof; QR remains the printed backup.
      </p>

      <ul className="materialverify__list" aria-label="Checkpoint material verification">
        {attentionRows.map((row, index) => (
          <li key={row.routeCheckpointId || row.checkpointId || `${row.checkpointName}-${index}`} className="materialverify__item">
            <div className="materialverify__head">
              <span className="materialverify__name">
                {row.checkpointName}
                {row.isRequired ? <span className="ckrollout__req">required</span> : null}
              </span>
              <span className={`materialverify__status materialverify__status--${row.verificationStatus}`}>
                {materialVerificationLabel(row.verificationStatus)}
              </span>
            </div>
            <div className="materialverify__where">
              {[row.routeName, row.siteName].filter(Boolean).join(" · ") || "No route"}
            </div>
            <div className="materialverify__meta">
              <span>Material: {row.activeCheckpointMaterial}</span>
              <span>NFC: {formatTimestamp(row.lastNfcScanAt)}</span>
              <span>QR: {formatTimestamp(row.lastQrScanAt)}</span>
            </div>
            {rolloutByCheckpointId.get(row.checkpointId) ? (
              <ActiveMaterialBadge row={rolloutByCheckpointId.get(row.checkpointId)!} />
            ) : null}
            <div className="materialverify__action">Next: {materialVerificationAction(row.verificationStatus)}</div>
            {mintEnabled ? (
              <div className="materialverify__actions">
                {session.hasSessionKit(row.checkpointId) ? (
                  <button
                    type="button"
                    className="builder__navbtn"
                    aria-label={`View kit for ${row.checkpointName}`}
                    onClick={() => session.openSessionKit(row.checkpointId)}
                  >
                    View kit
                  </button>
                ) : null}
                <button
                  type="button"
                  className={row.hasCheckpointMaterial ? "builder__navbtn" : "minter__confirm-go"}
                  aria-label={
                    row.hasCheckpointMaterial
                      ? `Replace material for ${row.checkpointName}`
                      : `Create material for ${row.checkpointName}`
                  }
                  onClick={() => openMintForRow(row)}
                >
                  {row.hasCheckpointMaterial ? "Replace material" : "Create material"}
                </button>
              </div>
            ) : null}
          </li>
        ))}
      </ul>

      {readyRows.length > 0 ? (
        <div className="materialverify__ready" aria-label="Fully verified checkpoints">
          {readyRows.map((row, index) => (
            <span key={row.routeCheckpointId || row.checkpointId || `${row.checkpointName}-${index}`} className="ckrollout__readychip">
              {row.checkpointName}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function verificationRank(status: MaterialVerificationStatus): number {
  switch (status) {
    case "missing_material":
      return 0;
    case "unverified":
      return 1;
    case "verified_backup":
      return 2;
    case "verified_primary":
      return 3;
    case "verified_both":
      return 4;
  }
}

function formatTimestamp(value: string | null): string {
  if (value === null) return "not yet";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "recorded";
  return parsed.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}
